import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useFrontAuthStore } from '../store/authStore';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ALL_ROUTES } from '../../../core/router/routes';
import DarkModeToggle from '../../components/ui/DarkModeToggle';
import { useTheme } from '../../../core/contexts/ThemeContext';
import env from '../../../core/config/env';

const LoginContainer = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  pointer-events: none;
  background:
    url('/images/global/member_bg_bottom1.png') no-repeat left bottom,
    url('/images/global/member_bg_bottom2.png') no-repeat right bottom,
    url('/images/global/member_bg.png') no-repeat center center;
  background-size: auto, auto, cover;

  html.dark-mode & {
    background:
      url('/images/global/member_bg_bottom1_dark.png') no-repeat left bottom,
      url('/images/global/member_bg_bottom2_dark.png') no-repeat right bottom,
      url('/images/global/member_bg_dark.png') no-repeat center center;
  }
`;


const Header = styled.header`
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.xl};
  & button {
    pointer-events: auto;
  }
`;

const Logo = styled.div`
  cursor: pointer;
  pointer-events: auto;

  html.dark-mode & img {
    filter: invert(1);
  }
`;



const LoginCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 320px;
  gap: ${props => props.theme.spacing['2xl']};
  pointer-events: auto;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSize['2xl']};
  font-weight: ${props => props.theme.fontWeight.semibold};
  line-height: 141%;
  color: ${props => props.theme.colors.book.color1b};
  text-align: center;
`;

const Form = styled.form`
  width: 100%;
  padding-top: ${props => props.theme.spacing['3xl']};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing['2xl']};
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const LabelText = styled.label`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
  color: ${props => props.theme.colors.book.color6b};
  & > .required {
    color: #DF0707;
    font-size: ${props => props.theme.fontSize.xs};
  }
`;

const Input = styled.input<{ $hasError: boolean }>`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.$hasError ? props.theme.colors.error[300] : props.theme.colors.white};
  background-color: ${props => props.theme.colors.white};
  font-size: ${props => props.theme.fontSize.base};
  color: ${props => props.theme.colors.book.color2b};
  transition: all 0.2s;
  height: 44px;
  width: 100%;
  &:focus {
    outline: none;
  }
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px ${props => props.theme.colors.white} inset !important;
    -webkit-text-fill-color: ${props => props.theme.colors.book.color2b} !important;
    transition: background-color 5000s ease-in-out 0s;
  }

  &::placeholder {
    color: ${props => props.theme.colors.gray[400]};
  }

`;

const IdSaveCheckbox = styled.div<{ $checked: boolean;}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  & input {
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: ${props => props.theme.borderRadius.sm};
    border: 1px solid ${props => props.$checked ? props.theme.colors.toss.blue : props.theme.colors.book.color8b};
    background-color: ${props => props.$checked ? props.theme.colors.toss.blue : '#F4F4F4'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s;
    outline: none;
    &:focus {
      outline: none;
    }
    &:hover {
      cursor: pointer;
    }
    &::after {
      content: '';
      display: ${props => props.$checked ? 'block' : 'none'};
      width: 3px;
      height: 8px;
      border: solid white;
      border-width: 0 2px 2px 0;
      border-radius: 2px;
      transform: rotate(45deg) translateY(0px) translateX(-1px);
    }
  }
`;


const Button = styled.button<{ $disabled: boolean; $loading: boolean }>`
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: ${props => props.theme.fontWeight.semibold};
  border-radius: ${props => props.theme.borderRadius.lg};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  width: 100%;
  padding: ${props => `${props.theme.spacing.md} ${props.theme.spacing.lg}`};
  height: 44px;
  font-size: ${props => props.theme.fontSize.base};
  background-color: ${props => props.$disabled ? props.theme.colors.book.color4b : props.theme.colors.book.color0b};
  color: ${props => props.theme.colors.white};


  ${props => (props.$disabled || props.$loading) && `
    opacity: 0.8;
    background-color: ${props.theme.colors.book.color4b};
    cursor: not-allowed;
    transform: none !important;
  `}

  ${props => props.$loading && `
    color: transparent;
    
    &::after {
      content: '';
      position: absolute;
      width: 1rem;
      height: 1rem;
      border-top: 2px solid transparent;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
    
    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }
  `}
`;

const ErrorAlert = styled.div`
  background-color: ${props => props.theme.colors.red[50]};
  border: 1px solid ${props => props.theme.colors.red[200]};
  color: ${props => props.theme.colors.red[600]};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
`;

const LinkText = styled.p`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${props => props.theme.spacing.xl};
  text-align: center;
  margin-top: ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.book.color6b};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.normal};
  & a {
    color: ${props => props.theme.colors.book.color6b};
    font-weight: ${props => props.theme.fontWeight.normal};
    text-decoration: underline;
    text-underline-offset: 3px;
    &:hover {
      color: ${props => props.theme.colors.primary[900]};
    }
  }
`;

const StyledLink = styled(Link)`
  color: ${props => props.theme.colors.toss.blue};
  font-weight: ${props => props.theme.fontWeight.semibold};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const MockAccountInfo = styled.div`
  margin-top: ${props => props.theme.spacing['2xl']};
  padding: ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.gray[50]};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px dashed ${props => props.theme.colors.gray[300]};
`;

const MockTitle = styled.div`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.gray[700]};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const MockAccount = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  margin-bottom: ${props => props.theme.spacing.sm};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const MockLabel = styled.span`
  font-size: ${props => props.theme.fontSize.xs};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.gray[600]};
`;

const MockValue = styled.span`
  font-size: ${props => props.theme.fontSize.xs};
  color: ${props => props.theme.colors.gray[700]};
  font-family: monospace;
`;

const SAVED_ID_KEY = 'saved_login_id';

const FrontLoginForm: React.FC = () => {
  const savedId = localStorage.getItem(SAVED_ID_KEY);
  const [formData, setFormData] = useState({
    username: savedId || '',
    password: '',
    idSave: !!savedId,
  });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { login, isAuthenticated } = useFrontAuthStore();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated) {
      navigate(ALL_ROUTES.BOOK_LIST, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (formData.idSave) {
      localStorage.setItem(SAVED_ID_KEY, formData.username);
    } else {
      localStorage.removeItem(SAVED_ID_KEY);
    }

    try {
      await login(formData.username, formData.password);
      // 로그인 성공 후 리다이렉트 (책 클릭으로 로그인한 경우 해당 책으로 이동)
      const redirectPath = localStorage.getItem('redirect_after_login');
      if (redirectPath) {
        localStorage.removeItem('redirect_after_login');
        navigate(redirectPath, { replace: true });
      } else {
        const from = (location.state as any)?.from?.pathname || ALL_ROUTES.BOOK_LIST;
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '로그인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  return (
    <LoginContainer>
      <Header>
        <Logo onClick={() => navigate('/')}><img src="/images/global/logo.svg" alt="logo" /></Logo>
        <DarkModeToggle isDark={isDarkMode} onToggle={toggleDarkMode} />
      </Header>
      <LoginCard>
        <Logo onClick={() => navigate('/')}><img src="/images/global/logo.svg" alt="logo" /></Logo>
        <Title>모든 과정이 학습이 되는<br/>나만의 스마트 전자책</Title>

        <Form onSubmit={handleSubmit}>
          {error && <ErrorAlert>{error}</ErrorAlert>}
          
          <FormField>
            <LabelText htmlFor="username">
              아이디 <span className="required">*</span>
            </LabelText>
            <Input
              id="username"
              type="text"
              placeholder="아이디를 입력하세요"
              value={formData.username}
              onChange={(e) => handleInputChange('username', e.target.value)}
              $hasError={!!error}
              required
            />
          </FormField>

          <FormField>
            <LabelText htmlFor="password">
              비밀번호 <span className="required">*</span>
            </LabelText>
            <Input
              id="password"
              type="password"
              placeholder="비밀번호를 입력하세요"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              $hasError={!!error}
              required
            />
          </FormField>
          <IdSaveCheckbox $checked={formData.idSave}>
            <input
              id="id-save"
              type="checkbox"
              checked={formData.idSave}
              onChange={(e) => setFormData(prev => ({ ...prev, idSave: e.target.checked }))}
            />
            <LabelText htmlFor="id-save">
              아이디 저장
            </LabelText>
          </IdSaveCheckbox>

          <Button
            type="submit"
            $disabled={loading}
            $loading={loading}
          >
            {loading ? '로그인 중...' : '로그인'}
          </Button>
        </Form>

        <LinkText style={{ marginTop: '0.5rem' }}>
          <StyledLink to="/find-id">아이디 찾기</StyledLink> | <StyledLink to="/reset-password">비밀번호 재설정</StyledLink>
        </LinkText>
        <LinkText>
          <span>듬뿍에 처음 방문하셨나요?</span> <StyledLink to="/register">회원가입</StyledLink>
        </LinkText>

        {/* 개발용 테스트 계정 정보 - 로컬 환경에서만 표시 */}
        {env.REACT_APP_ENV === 'local' && process.env.NODE_ENV !== 'production' && (
          <MockAccountInfo>
            <MockTitle>테스트 계정</MockTitle>
            <MockAccount>
              <MockLabel>학생 계정:</MockLabel>
              <MockValue>student-2 / student-2</MockValue>
            </MockAccount>
            <MockAccount>
              <MockLabel>교수자 계정:</MockLabel>
              <MockValue>professor-1 / professor-1</MockValue>
            </MockAccount>
          </MockAccountInfo>
        )}
      </LoginCard>
    </LoginContainer>
  );
};

export default FrontLoginForm;
